import * as THREE from 'three';
// @ts-ignore
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { Octree } from 'three/examples/jsm/math/Octree.js';
// import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { isMobile } from '../utils/index.ts';
import { getGaussianSplattingRender } from './render/index.ts';
import { getElementSize, isWeixin } from '../../../../utils/index.ts';
import { resizeEventListener } from '../../../utils/window.ts';

interface SceneConfig {
  avatarInfo?: {
    camera?: {
      position: { x: number; y: number; z: number };
      target: { x: number; y: number; z: number };
    };
  };
}
export class SceneController {
  private container: HTMLElement;
  private scene?: THREE.Scene;
  private frameCount = 0;
  private camera: THREE.PerspectiveCamera | null = null;
  private renderer: THREE.WebGLRenderer | null = null;
  private orbitControl: OrbitControls | null = null;
  private worldOctree: Octree | null = null;
  private cameraTarget: THREE.Vector3;
  private cameraOffset: THREE.Vector3;
  private smoothFactor: number;
  private isFollowingPlayer: boolean;
  private isOrbiting: boolean;
  private orbitRadius: number;
  private orbitAngle: number;
  private orbitSpeed: number;
  private orbitCenter: THREE.Vector3;
  private orbitHeight: number;
  private savedCameraPosition: THREE.Vector3 | null = null;
  private savedCameraTarget: THREE.Vector3 | null = null;
  private lastPlayerPosition: THREE.Vector3;
  private isMobile: boolean;
  private offset: number;
  private rendererScale: number;
  private rootElement: HTMLElement;
  private config: SceneConfig | null = null;
  private gsRender: any; // TODO: 定义更具体的类型
  private isWeixin: boolean;
  private cameraY: number;

  constructor(container: HTMLElement) {
    this.container = container;
    this.cameraTarget = new THREE.Vector3();
    // 摄像机跟随设置
    this.cameraOffset = new THREE.Vector3();
    this.smoothFactor = 0.1;
    this.isFollowingPlayer = true;
    // 环绕观察设置
    this.isOrbiting = false;
    this.orbitRadius = 4;
    this.orbitAngle = 0;
    this.orbitSpeed = 0.002; // 环绕速度（原来的1/3）
    this.orbitCenter = new THREE.Vector3();
    this.orbitHeight = 0; // 保存环绕时的摄像机高度
    this.savedCameraPosition = null; // 保存环绕前的摄像机位置
    this.savedCameraTarget = null; // 保存环绕前的摄像机目标
    // 记录上一帧的玩家位置
    this.lastPlayerPosition = new THREE.Vector3();
    this.isMobile = isMobile();
    this.offset = this.isMobileVerticalScreen() ? 1 : 0;
    // 画布缩放
    this.rendererScale = 1;
    this.rootElement = document.createElement('div');
    this.isWeixin = isWeixin();
    this.cameraY = this.isWeixin ? 1.6 : 1.45;
  }

  async initScene(
    config: any,
    onProgress?: (percent: number, percentLabel: string, loaderStatus: number) => void,
  ): Promise<void> {
    this.config = config;
    // 创建场景
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xa8def0);
    // 创建碰撞检测八叉树
    this.worldOctree = new Octree();
    // 创建相机
    this.camera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 0.1, 300);
    this.camera.position.y = 5;
    this.camera.position.z = 5;
    this.camera.position.x = 0;
    this.camera.position.set(0, 2, 10);

    // 创建渲染器
    this.renderer = new THREE.WebGLRenderer({
      antialias: false, // 移动设备禁用抗锯齿
      powerPreference: 'high-performance', // 优先使用高性能模式
    });
    this.setRenderScale(this.container);
    // const pixelRatio = this.isMobile
    //   ? Math.min(1.5, window.devicePixelRatio) // 移动设备限制最大像素比为1.5
    //   : window.devicePixelRatio;
    // this.renderer.setPixelRatio(pixelRatio);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    // 将渲染器添加到DOM
    this.rootElement.appendChild(this.renderer.domElement);
    this.container.appendChild(this.rootElement);

    // 创建控制器
    this.orbitControl = new OrbitControls(this.camera, this.renderer.domElement);
    this.orbitControl.enableDamping = true;
    this.orbitControl.minDistance = 4 + this.offset;
    this.orbitControl.maxDistance = 4 + this.offset;
    this.orbitControl.enablePan = false;
    this.orbitControl.maxPolarAngle = Math.PI / 2 + 0.8;
    this.orbitControl.update();

    // 添加灯光
    this.addLights();

    // 加载碰撞世界
    // this.loadCollisionWorld();
    this.gsRender = getGaussianSplattingRender(this);
    await this.gsRender.render(this.config, this.rootElement, undefined, onProgress);
    // 如果有设置摄像机的信息
    if (this.config?.avatarInfo?.camera) {
      this.camera.position.set(
        this.config?.avatarInfo?.camera?.position.x,
        this.config?.avatarInfo?.camera?.position.y,
        this.config?.avatarInfo?.camera?.position.z,
      );
      this.camera.lookAt(
        this.config?.avatarInfo?.camera?.target.x,
        this.config?.avatarInfo?.camera?.target.y,
        this.config?.avatarInfo?.camera?.target.z,
      );
    }
    // 添加窗口大小调整监听器
    window.addEventListener('resize', this.onWindowResize.bind(this));

    resizeEventListener(this.camera, this.renderer, this.rootElement, this.container);
  }
  addLights(): void {
    const ambientLight = new THREE.AmbientLight(0x6688cc, 0.6);
    this.scene?.add(ambientLight);

    // 主方向光 - 用于产生阴影
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1.2);
    directionalLight.position.set(-5, 25, -1);
    directionalLight.castShadow = true;

    // 配置阴影参数
    if (this.isMobile) {
      directionalLight.shadow.mapSize.width = 512;
      directionalLight.shadow.mapSize.height = 512;
      directionalLight.shadow.radius = 2;
    } else {
      directionalLight.shadow.mapSize.width = 2048; // 增加阴影贴图分辨率
      directionalLight.shadow.mapSize.height = 2048;
      directionalLight.shadow.radius = 3;
    }

    // 配置阴影相机
    directionalLight.shadow.camera.near = 0.1;
    directionalLight.shadow.camera.far = 500;
    directionalLight.shadow.camera.right = 30;
    directionalLight.shadow.camera.left = -30;
    directionalLight.shadow.camera.top = 30;
    directionalLight.shadow.camera.bottom = -30;
    directionalLight.shadow.bias = -0.0001;
    this.scene?.add(directionalLight);
  }
  onWindowResize(): void {
    this.gsRender.onWindowResize();
  }
  animate(playerPosition?: THREE.Vector3): void {
    // 更新环绕观察
    this.updateOrbiting();

    // 动态调整OrbitControls摇臂长度
    this.adjustCameraDistance();
    this.frameCount += 1;
    if (this.frameCount % 2 === 0) {
      this.renderer?.render(this.scene!, this.camera!);
      this.gsRender.animate(playerPosition);
    }
  }

  adjustCameraDistance(): void {
    if (!this.orbitControl) return;

    // 获取当前摄像机的极角（垂直角度）
    const polarAngle = this.orbitControl.getPolarAngle();

    // 定义临界角度：当角度大于此值时认为接近地面
    const groundThreshold = Math.PI / 2; // 90度，水平角度
    const maxAngle = Math.PI / 2 + 0.8; // 最大允许角度

    let targetDistance;
    // 判断是否接近地面
    if (polarAngle <= groundThreshold) {
      // 离开地面时，恢复摇臂长度为4
      targetDistance = 4;
    } else {
      // 超过临界角度后，根据角度差值平滑过渡
      const angleDiff = polarAngle - groundThreshold; // 超出临界角度的部分
      const maxAngleDiff = maxAngle - groundThreshold; // 最大角度差

      // 计算过渡因子（0到1之间）
      const transitionFactor = Math.min(1, angleDiff / maxAngleDiff);

      // 从4平滑过渡到2
      const minDistance = 2;
      const maxDistance = 4 + this.offset;
      targetDistance = maxDistance - (maxDistance - minDistance) * transitionFactor;
    }

    // 平滑调整摇臂长度
    this.orbitControl.maxDistance = THREE.MathUtils.lerp(
      this.orbitControl.maxDistance,
      targetDistance,
      0.1, // 平滑因子
    );

    // 同时调整当前距离以立即生效
    const currentDistance = this.orbitControl.getDistance();
    if (currentDistance > this.orbitControl.maxDistance) {
      // 直接设置摄像机位置来调整距离
      const direction = this.camera?.position.clone().sub(this.orbitControl.target).normalize();
      this.camera?.position
        .copy(this.orbitControl.target)
        .add(direction!.multiplyScalar(this.orbitControl.maxDistance));
    }
  }
  isMobileVerticalScreen(): boolean {
    return this.isMobile && window.innerHeight > window.innerWidth;
  }
  getScene(): THREE.Scene {
    return this.scene!;
  }

  getCamera(): THREE.PerspectiveCamera {
    return this.camera!;
  }

  getRenderer(): THREE.WebGLRenderer | null {
    return this.renderer;
  }

  getOrbitControl(): OrbitControls | null {
    return this.orbitControl;
  }

  getWorldOctree(): Octree | null {
    return this.worldOctree;
  }

  initializeCameraTarget(playerPosition: THREE.Vector3): void {
    this.cameraTarget.copy(playerPosition);
    this.cameraTarget.y += this.cameraY; // 调整到角色头部高度
    this.lastPlayerPosition.copy(playerPosition);
    if (this.orbitControl) {
      this.orbitControl.target = this.cameraTarget;
    }
  }
  updateCamera(playerPosition: THREE.Vector3): void {
    if (!this.isFollowingPlayer) return;

    // 计算玩家位置的变化
    const deltaPosition = playerPosition.clone().sub(this.lastPlayerPosition);

    // 更新摄像机位置以跟随玩家
    if (this.camera) {
      this.camera.position.add(deltaPosition);
    }

    // 更新摄像机目标
    this.cameraTarget.copy(playerPosition);
    // this.cameraTarget.y += 1.7; // 调整到角色头部高度
    this.cameraTarget.y += this.cameraY;
    if (this.orbitControl) {
      this.orbitControl.target = this.cameraTarget;
    }

    // 记录当前玩家位置
    this.lastPlayerPosition.copy(playerPosition);
  }

  setCameraAndOribitControl(cameraInfo: {
    position: { x: number; y: number; z: number };
    target: { x: number; y: number; z: number };
    azimuthalAngle: number;
    distance: number;
    polarAngle: number;
  }): void {
    const camera = this.getCamera();
    if (camera) {
      camera.position.set(cameraInfo.position.x, cameraInfo.position.y, cameraInfo.position.z);
    }
    const orbitControl = this.getOrbitControl();

    // 首先设置摄像头目标
    if (orbitControl) {
      orbitControl.target.set(cameraInfo.target.x, cameraInfo.target.y, cameraInfo.target.z);
      orbitControl.azimuthalAngle = cameraInfo.azimuthalAngle;
      orbitControl.polarAngle = cameraInfo.distance;
      orbitControl.distance = cameraInfo.polarAngle;

      // 更新OrbitControls
      orbitControl.update();
    }
  }
  smoothUpdateCamera(playerPosition: THREE.Vector3): void {
    if (!this.isFollowingPlayer) return;

    // 平滑跟随玩家
    const targetCameraPosition = playerPosition.clone();
    targetCameraPosition.add(this.cameraOffset);

    if (this.camera) {
      this.camera.position.lerp(targetCameraPosition, this.smoothFactor);
    }

    // 更新摄像机目标
    this.cameraTarget.copy(playerPosition);
    this.cameraTarget.y += this.cameraY; // 调整到角色头部高度
    if (this.orbitControl) {
      this.orbitControl.target.lerp(this.cameraTarget, this.smoothFactor);
    }
  }

  setCameraOffset(offset: THREE.Vector3): void {
    this.cameraOffset.copy(offset);
  }

  setFollowPlayer(follow: boolean): void {
    this.isFollowingPlayer = follow;
  }

  setSmoothFactor(factor: number): void {
    this.smoothFactor = Math.max(0, Math.min(1, factor));
  }

  // 摄像机控制方法
  enableOrbitControls(): void {
    if (this.orbitControl) {
      this.orbitControl.enabled = true;
    }
  }

  disableOrbitControls(): void {
    if (this.orbitControl) {
      this.orbitControl.enabled = false;
    }
  }

  // 开始环绕观察
  startOrbiting(playerPosition: THREE.Vector3): void {
    this.isOrbiting = true;
    this.orbitCenter.copy(playerPosition);
    this.orbitCenter.y += this.cameraY; // 调整到角色头部高度

    // 保存当前摄像机状态
    this.savedCameraPosition = this.camera!.position.clone();
    this.savedCameraTarget = this.orbitControl.target.clone();

    // 计算当前摄像机相对于玩家的角度和距离
    const cameraDirection = this.camera!.position.clone().sub(this.orbitCenter);
    this.orbitAngle = Math.atan2(cameraDirection.x, cameraDirection.z);
    this.orbitRadius = Math.sqrt(cameraDirection.x * cameraDirection.x + cameraDirection.z * cameraDirection.z);
    this.orbitHeight = this.camera!.position.y; // 保存当前高度

    // 禁用OrbitControls以避免冲突
    this.disableOrbitControls();
  }

  // 停止环绕观察
  stopOrbiting(): void {
    this.isOrbiting = false;

    // 恢复OrbitControls的目标位置
    if (this.savedCameraTarget && this.orbitControl) {
      this.orbitControl.target.copy(this.savedCameraTarget);
    }

    // 重新启用OrbitControls
    this.enableOrbitControls();

    // 更新OrbitControls以应用新的状态
    this.orbitControl.update();
  }

  // 更新环绕观察
  updateOrbiting(): void {
    if (!this.isOrbiting) return;

    // 增加环绕角度
    this.orbitAngle += this.orbitSpeed;

    // 计算新的摄像机位置，使用保存的高度和半径
    const x = this.orbitCenter.x + Math.sin(this.orbitAngle) * this.orbitRadius;
    const z = this.orbitCenter.z + Math.cos(this.orbitAngle) * this.orbitRadius;

    // 使用保存的高度，不受OrbitControls影响
    this.camera?.position.set(x, this.orbitHeight, z);

    // 让摄像机始终看向玩家
    this.camera?.lookAt(this.orbitCenter);
  }

  resetCamera(playerPosition: THREE.Vector3): void {
    this.initializeCameraTarget(playerPosition);
  }
  showInfoPanel(visible: boolean): void {
    if (this.gsRender) {
      this.gsRender.showInfoPanel(visible);
    }
  }
  // 场景相关的其他方法
  updateLighting(): void {
    // 可以在这里添加动态光照逻辑
  }

  updateEnvironment(): void {
    // 可以在这里添加环境更新逻辑
  }

  /**
   * 设置渲染比例
   * @param {*} scale
   * @param {*} wrapper
   */
  setRenderScale(wrapper: HTMLElement): void {
    const size = getElementSize(wrapper || document.body);
    this.rendererScale = Math.min(1, Math.sqrt((1024 * 768) / (size.width * size.height)));
    console.log('setRenderScale===============', this.rendererScale);
    this.renderer!.domElement.style.transform = `scale(${1 / this.rendererScale})`;
    this.renderer!.domElement.style.transformOrigin = '0 0';
    const width = size.width * this.rendererScale;
    const height = size.height * this.rendererScale;
    this.renderer!.setSize(width, height);

    this.rootElement.style.width = `${width}px`;
    this.rootElement.style.height = `${height}px`;
    // this.rootElement.style.transform = `scale(${1 / this.rendererScale})`;
    this.rootElement.style.transformOrigin = '0 0';
  }
}
